import { useRef, useEffect, useCallback } from 'react'

/**
 * Custom hook to track component mount status
 * 
 * This hook helps prevent memory leaks by providing a way to check
 * if a component is still mounted before performing async operations
 * like setState, API calls, or interval callbacks.
 * 
 * Usage:
 *   const isMounted = useIsMounted()
 *   
 *   // In async operations:
 *   if (!isMounted()) return
 *   setState(...)
 * 
 * @returns {Function} Function that returns true when mounted, false when unmounted
 */
export const useIsMounted = () => {
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Return a stable function that checks the ref
  const isMounted = useCallback(() => {
    return isMountedRef.current
  }, [])

  return isMounted
}

export default useIsMounted